import HeadMetadata from 'components/shared/HeadMetadata';
import { useI18n } from 'hooks/useI18n';
import { usePageTrack } from 'hooks/usePageTrack';
import { GetStaticProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Script from 'next/script';
import WPService from 'services/wp.service';
import { APP_URLS } from 'utils/app-urls';
import { getPublicPath } from 'utils/paths';
import { avoidRateLimit } from 'utils/rate-limit';
import { WP_REST_API_Categories, WP_REST_API_Posts } from 'wp-types';
import BlogComponent from '../../components/marketingSite/blog/BlogComponent';
import { hiddenCategories } from 'components/marketingSite/blog/utils/blogUtils';

type BlogPageProps = {
  posts?: WP_REST_API_Posts;
  categories?: WP_REST_API_Categories;
  totalPosts: number;
  totalPages: number;
};

const BlogPage: React.FC<BlogPageProps> = (props) => {
  const { t } = useI18n('blog');

  usePageTrack('Blog - Page Loaded');

  return (
    <>
      <HeadMetadata
        title={t('pageTitle')}
        desc={t('pageDescription')}
        imgUrl={getPublicPath('/images/card_blog.png')}
        url={APP_URLS.blog}
        indexable={true}
      />

      <Script
        id="blogMetadata"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: `{
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "${t('pageTitle')}",
            "url": "https://www.getmanfred.com/blog",
            "description": "${t('pageDescription')}",
            "publisher": {
              "@type": "Organization",
              "name": "Manfred",
              "logo": {
                "@type": "ImageObject",
                "url": "https://media-asgard.s3.eu-west-1.amazonaws.com/manfred-media/logo_manfred_color.svg?v=38732000",
                "width": 200,
                "height": 50
              }
            }
          }`
        }}
      />

      <BlogComponent {...props} currentPage={0} />
    </>
  );
};

export default BlogPage;

export const getStaticProps: GetStaticProps<BlogPageProps> = async ({
  locale
}) => {
  const responsePosts = await WPService.getPosts({
    locale: locale || 'en'
  });
  const featuredPosts = await WPService.getPosts({
    featured: 'include',
    locale: locale || 'en'
  });

  await avoidRateLimit();

  const categories = (await WPService.getAllCategories(locale || 'en')).filter(
    (c) =>
      hiddenCategories.includes(c.slug as typeof hiddenCategories[number]) ===
      false
  );
  const tagsInfo = await WPService.getAllTags(locale || 'en');

  return {
    props: {
      ...(await serverSideTranslations(locale || '', [
        'common',
        'topbar',
        'blog',
        'footer'
      ])),
      posts: responsePosts?.data,
      featuredPosts: featuredPosts?.data,
      totalPosts: Number(responsePosts?.headers.get('x-wp-total') || ''),
      totalPages: Number(responsePosts?.headers.get('x-wp-totalpages') || ''),
      categories,
      tags: tagsInfo
    },
    revalidate: 10
  };
};
